<script lang="ts">
    export let images: {
        url: string;
        caption: string;
        isAdult: boolean;
        credits: string;
        imageId: string;
    }[];
    export let imageToShowIndex = 0;
    export let imageToShow = images[imageToShowIndex];
    import {
        COMMON__details,
        COMMON__settings,
        GALLERY__adult__message,
        GALLERY__adult__title,
    } from "@app/services/i18n.messages";
    import { getMessage } from "@app/services/i18n.service";
    import { isMobile } from "@app/services/isMobile";
    import { onMount } from "svelte";
    import Modal from "../Modal.svelte";
    import GlosbeImage from "./GlosbeImage.svelte";

    export let onClose: () => void = () => {};

    function switchImage(event: Event) {
        const trigger = event.currentTarget as HTMLButtonElement;
        const imageIndex = parseInt(trigger.dataset["imageindex"]);
        if (!isNaN(imageIndex) && images[imageIndex]) {
            imageToShowIndex = imageIndex;
            imageToShow = images[imageIndex];
        }
    }

    let containerElement: HTMLDivElement;
    function onContainerClick(event: Event) {
        if (event.target === containerElement && onClose) {
            onClose();
        }
    }

    function navigateImagesWithKeys(event: KeyboardEvent) {
        let delta = 0;
        if (event.key === "ArrowLeft") {
            delta = -1;
        } else if (event.key === "ArrowRight") {
            delta = 1;
        }
        if (images.length === 0) {
            return;
        }
        if (delta != 0) {
            event.stopPropagation();
            event.preventDefault();
        }
        let imageIndex =
            (images.length + imageToShowIndex + delta) % images.length;

        imageToShowIndex = imageIndex;
        imageToShow = images[imageIndex];
    }
    let imagedetailsImageContainer: HTMLDivElement;

    function updateImageDetailsImageContainerMaxDimmensions() {
        if (isMobile()) {
            return;
        }
        if (!imagedetailsImageContainer) {
            return;
        }
        const img = imagedetailsImageContainer.querySelector("img");
        if (!img) {
            return;
        }
        const maxWidth =
            imagedetailsImageContainer.parentElement.clientWidth + "px";

        const maxHeight =
            imagedetailsImageContainer.parentElement.clientHeight * 0.8 + "px";

        img.style.maxWidth = maxWidth;
        img.style.maxHeight = maxHeight;
    }

    $: {
        updateImageDetailsImageContainerMaxDimmensions();
    }

    onMount(() => {
        updateImageDetailsImageContainerMaxDimmensions();
    });

    const settingsUrl = "/settings";
</script>

<svelte:window
    on:keydown={navigateImagesWithKeys}
    on:resize={updateImageDetailsImageContainerMaxDimmensions}
/>
<Modal open={true} {onClose}>
    <div
        class="h-full w-full md:p-20 relative cursor-pointer imagebrowser"
        on:click={onContainerClick}
        bind:this={containerElement}
    >
        <div class="absolute right-0 top-0 p-1 md:p-4 z-10">
            <button
                on:click={onClose}
                data-element="image-gallery__close"
                class="text-white bg-gray-700 w-12 h-12 rounded-full text-2xl
          hover:bg-gray-600"
            >
                <span class="icon-close" />
            </button>
        </div>
        <div
            class="flex bg-white w-full h-full md:rounded-lg md:shadow-lg
        overflow-hidden cursor-default"
            data-testid="images-gallery"
        >
            <div class="md:w-4/5 w-full bg-gray-800 flex flex-col flex">
                <div
                    class="relative items-center justify-center flex flex-1 imagebrowser__imagesdetails__image"
                    bind:this={imagedetailsImageContainer}
                >
                    {#if imageToShow.isAdult}
                        <div class="flex flex-col text-white text-center">
                            <p>
                                <span
                                    data-element="image-gallery__image__adult"
                                    class="image-gallery__image__adult"
                                />
                            </p>
                            <p class="text-xl font-medium py-4">
                                {getMessage(GALLERY__adult__title)}
                            </p>
                            <p class="text-sm">
                                {getMessage(GALLERY__adult__message)}
                            </p>
                            <p class="py-4">
                                <a
                                    href={settingsUrl}
                                    class="text-decoration-none rounded bg-white text-gray-700 uppercase px-4 py-2 text-xs hover:bg-gray-200"
                                    >{getMessage(COMMON__settings)}
                                </a>
                            </p>
                        </div>
                    {:else}
                        <GlosbeImage
                            url={imageToShow.url}
                            class="object-contain"
                            alt={imageToShow.caption}
                            sizeMappings={[
                                { constraint: "(max-width:360px)", size: 360 },
                                { constraint: "(max-width:720px)", size: 720 },
                                { constraint: "(min-width:980px)", size: 980 },
                            ]}
                        />
                    {/if}
                    <button
                        class="text-white absolute left-0 bg-gray-500 w-12 h-12
          rounded-full text-2xl bg-opacity-50 hover:bg-gray-100
          hover:bg-opacity-25 absolute"
                        data-element="images-gallery__prev-image"
                        on:click={switchImage}
                        data-imageindex={(images.length +
                            imageToShowIndex -
                            1) %
                            images.length}
                    >
                        <span class="icon-chevron-left" />
                    </button>
                    <button
                        class="text-white absolute right-0 bg-gray-500 w-12 h-12
              rounded-full text-2xl bg-opacity-50 hover:bg-gray-100
              hover:bg-opacity-25 absolute"
                        on:click={switchImage}
                        data-element="images-gallery__next-image"
                        data-imageindex={(images.length +
                            imageToShowIndex +
                            1) %
                            images.length}
                    >
                        <span class="icon-chevron-right" />
                    </button>
                </div>
                <div
                    class="bg-white h-32 text-md p-2 md:p-4 imagebrowser__imagescontainer__caption border-l border-r border-gray-300 overflow-y-auto"
                >
                    <p class="text-lg text-gray-800 font-medium">
                        <strong>{imageToShow.caption}</strong>
                    </p>
                    <p class="text-gray-600 text-sm py-1 md:py-2">
                        {imageToShow.credits}
                    </p>
                    <p class="text-sm">
                        <a
                            href="https://app.glosbe.com/image/{imageToShow.imageId}"
                            target="_blank">{getMessage(COMMON__details)}</a
                        >
                    </p>
                </div>
            </div>

            <div class="md:w-1/5 hidden md:block">
                <ul class="h-full overflow-y-scroll">
                    {#each images as image, index}
                        <li>
                            <button
                                class="text-align-unset w-full h-full border-l-8 flex transition-all duration-300
                   focus:outline-none px-4 py-2 items-center  text-gray-700
                  justify-center {index === imageToShowIndex
                                    ? 'border-blue-500 bg-blue-100'
                                    : 'border-transparent'}"
                                on:click={switchImage}
                                data-imageindex={index}
                            >
                                {#if !image.isAdult}
                                    <GlosbeImage
                                        url={image.url}
                                        sizeMappings={[{ size: 80 }]}
                                        alt={image.caption}
                                        class="imagebrowser__imageslist__image"
                                    />
                                {:else}
                                    <div
                                        class="imagebrowser__imageslist__image flex items-center justify-center bg-gray-800 p-2"
                                    >
                                        <div
                                            class=" image-gallery__preview__adult"
                                            data-element="image-gallery__preview__adult"
                                        />
                                    </div>
                                {/if}
                                <div class="flex-1 p-2">
                                    <p class=" line-clamp-2 text-sm">
                                        {image.caption}
                                    </p>
                                </div>
                            </button>
                        </li>
                    {/each}
                </ul>
            </div>
        </div>
    </div>
</Modal>

<style>
    @media (min-width: 768px) {
        .imagebrowser__imagesdetails__image {
            height: 80%;
        }
        .imagebrowser__imagescontainer__caption {
            height: 20%;
        }
    }
    .imagebrowser__imageslist__image {
        max-width: 80px;
        max-height: 80px;
        width: 80px;
        object-fit: contain;
        height: auto;
    }

    @media (max-width: 768) {
        .imagebrowser {
            padding-top: 4rem;
        }
    }
    .text-align-unset {
        text-align: unset;
    }
    .image-gallery__image__adult {
        display: inline-block;
        width: 48px;
        height: 48px;
        background: url(/assets/icons/icon-adult-content.svg) no-repeat center;
        background-size: contain;
    }
    .image-gallery__preview__adult {
        display: inline-block;
        width: 48px;
        height: 48px;
        background-image: url(/assets/icons/icon-adult-content.svg);
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
    }
    .text-decoration-none {
        text-decoration: none;
    }
</style>
